/**
 * @module Logger
 * @description Service for the application logging
 *    Levels configured:
 *    { crit: 1, error: 2, warn: 3, info: 4, debug: 5}
 * @example Usage example (with metadata):
 *    Logger.warn(`user ${user} not authorized`, { ip: req.ip, user: req.jwt.user});
 * @copyright Aura Health, Inc.
 */
const axios = require('axios');
const config = require('../config');
const { isClient, isProdMode } = require('../utils');

const {
  logger: { level },
  service: { name: service, version },
} = config;

/**
 * Logger
 */
const levels = {
  debug: 5,
  info: 4,
  warn: 3,
  error: 2,
  crit: 1,
  audit: 0,
};
// Request headers that are safe to log. Everything else is dropped, so the
// cookie and authorization headers never reach the logs.
const LOGGED_HEADERS = [
  'accept-language',
  'host',
  'referer',
  'user-agent',
  'x-cloud-trace-context',
  'x-forwarded-proto',
];

// Query parameters that carry a credential, such as the webview paywall's
// `sessionCookie`.
const CREDENTIAL_PARAMS =
  /([?&#])((?:access_|id_|refresh_)?token|code|session_?cookie|password|secret)=[^&#]*/gi;

function withoutCredentialParams(url) {
  if (typeof url !== 'string') return url;
  return url.replace(CREDENTIAL_PARAMS, '$1$2=[REDACTED]');
}

// A bearer credential, or a JWT (a Firebase ID token) on its own
const BEARER = /Bearer\s+[^\s"',;]+/gi;
const JWT = /eyJ[\w-]*\.[\w-]+\.[\w-]+/g;

// Scrubs the strings the logger writes as text: the message (also a meta or
// posted `message`), an error's message and the url. An error or axios
// object passed as the message becomes its scrubbed message, so its config
// is never serialised. Other values are never scanned.
function scrub(text) {
  if (isHttpObject(text)) return scrub(String(text.message ?? ''));
  if (typeof text !== 'string') return text;
  return text
    .replace(BEARER, 'Bearer [REDACTED]')
    .replace(JWT, '[REDACTED_JWT]');
}

const REDACTED = '[REDACTED]';

// Key names whose value is never logged, matched case-insensitively anywhere
// in the key, as in the services' logger. A two-word name allows `-` or `_`
// between its words.
const REDACTED_KEY_PATTERNS = [
  /access[-_]?key/,
  /api[-_]?key/,
  /authorization/,
  /billing[-_]?details/,
  /cookie/,
  /coupon/,
  /credential/,
  /email/,
  /gift[-_]?code/,
  /password/,
  /payment[-_]?method/,
  /private[-_]?key/,
  /promo[-_]?code/,
  /receipt/,
  /redeem[-_]?code/,
  /secret/,
  /session/,
  /signature/,
  /token/,
  /voucher/,
];

// Whole key names whose value is never logged, where a pattern would catch
// too much: `auth` would match `author` and `oauthProvider`.
const REDACTED_KEYS = [/auth/, /auth[-_]?id/, /branch[-_]?key/, /pwd/];

const REDACTED_KEY = new RegExp(
  [
    ...REDACTED_KEY_PATTERNS.map((pattern) => pattern.source),
    ...REDACTED_KEYS.map((key) => `^(${key.source})$`),
  ].join('|'),
  'i'
);

function isRedacted(key, value) {
  return value !== undefined && value !== null && REDACTED_KEY.test(key);
}

// Only a plain object has its keys checked: a Date, an array or a class
// instance is logged as it is
function isPlainObject(value) {
  if (value === null || typeof value !== 'object') return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

// An error, an axios error or response, or an axios error spread into a plain
// object: each carries the request config, Authorization header included
function isHttpObject(value) {
  return (
    value instanceof Error ||
    (value !== null &&
      typeof value === 'object' &&
      (value.isAxiosError === true ||
        (value.config !== null && typeof value.config === 'object')))
  );
}

// What an axios error or response holds about its request
const AXIOS_INTERNALS = ['config', 'headers', 'request', 'response'];

/**
 * @param {Object} error an error, or an axios error or response
 * @returns {Object} its name, message, code and status only
 */
function safeError(error) {
  const result = {};
  if (error.name !== undefined) result.name = error.name;
  if (error.message !== undefined) result.message = scrub(error.message);
  if (error.code !== undefined) result.code = error.code;
  const status = error.response?.status ?? error.status;
  if (status !== undefined) result.status = status;
  return result;
}

// Keys whose text is scrubbed of Bearer tokens and JWTs. A V8 stack starts
// with the error's message, so it is scrubbed like the message.
const TEXT_KEYS = ['message', 'stack'];

function redactValue(key, value) {
  if (isRedacted(key, value)) return REDACTED;
  if (TEXT_KEYS.includes(key)) return scrub(value);
  if (isHttpObject(value)) return safeError(value);
  if (key === 'url') return scrub(withoutCredentialParams(value));
  return value;
}

// The second level: a shallow copy with each redacted key replaced. Nothing
// deeper is walked.
function redactKeys(value) {
  if (isHttpObject(value)) return safeError(value);
  if (!isPlainObject(value)) return value;
  const result = {};
  Object.keys(value).forEach((key) => {
    result[key] = redactValue(key, value[key]);
  });
  return result;
}

/**
 * Makes log meta safe to print, the way the services' logger does: by key
 * name, two levels deep (`token`, `body.password`, `headers.authorization`),
 * never walking further, so a large or nested meta costs the same as a small
 * one. An error or axios object keeps only its name, message, code and
 * status. An envelope that spread an axios error (older builds did) keeps its
 * own fields and its status but not the request. An error passed as the
 * whole meta goes under `error`, so its message doesn't replace the log
 * message.
 * @param {*} meta
 * @returns {*} a redacted copy
 */
function redactFields(meta) {
  if (meta instanceof Error) return { error: safeError(meta) };
  if (!isPlainObject(meta)) return meta;
  const dropped = isHttpObject(meta) ? AXIOS_INTERNALS : [];
  const result = {};
  Object.keys(meta).forEach((key) => {
    if (dropped.includes(key)) return;
    const value = meta[key];
    if (isRedacted(key, value)) result[key] = REDACTED;
    else if (key === 'url' || TEXT_KEYS.includes(key)) {
      result[key] = redactValue(key, value);
    } else result[key] = redactKeys(value);
  });
  if (dropped.length > 0 && result.status === undefined) {
    const status = meta.response?.status;
    if (status !== undefined) result.status = status;
  }
  return result;
}

function safeHeaders(headers = {}) {
  const safe = {};
  LOGGED_HEADERS.forEach((name) => {
    if (headers[name] !== undefined) {
      safe[name] = withoutCredentialParams(headers[name]);
    }
  });
  return safe;
}

const stackdriver = {
  debug: 100,
  info: 200,
  warn: 400,
  error: 500,
  crit: 600,
  audit: 0,
};

class Logger {
  constructor() {
    this.isServerLogger = !isClient();
    this.baseMeta = {
      loggerId:
        globalThis.crypto?.randomUUID?.() ??
        `logger-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      serviceContext: {
        service,
        version,
      },
    };
    if (isProdMode() && isClient() && window.navigator) {
      const {
        appName,
        appCodeName,
        appVersion,
        platform,
        product,
        userAgent,
        vendor,
      } = window.navigator;
      this.baseMeta.browser = {
        appName,
        appCodeName,
        appVersion,
        platform,
        product,
        userAgent,
        vendor,
      };
    }
  }

  formatMeta(meta, logLevel) {
    const metaData = {
      ...this.baseMeta,
      ...redactFields(meta),
      severity: stackdriver[logLevel],
    };
    if (isClient()) {
      metaData.url = withoutCredentialParams(window.location.href);
    }
    metaData.timestamp = new Date().toISOString();
    return metaData;
  }

  addUserData(userData) {
    if (!userData) return;
    // Only ids go into logs: email and name are personal data.
    const { id, role } = userData;
    const { user = {} } = this.baseMeta;
    user.id = id;
    user.role = role;
    this.baseMeta.user = user;
  }

  log(message, meta, logLevel) {
    if (levels[logLevel] > levels[level]) return;

    const metaData = this.formatMeta(meta, logLevel);
    const safeMessage = scrub(message);
    this.logToServer(safeMessage, metaData);

    // No logs on client in production
    if (isProdMode() && isClient()) return;
    // eslint-disable-next-line no-console
    const logger = console[logLevel] || console.error;
    if (this.isServerLogger) {
      logger(JSON.stringify({ message: safeMessage, ...metaData }));
    } else {
      logger(safeMessage, metaData);
    }
  }

  logToServer(message, meta) {
    try {
      if (isClient()) {
        // Fire-and-forget: axios() returns a promise, so a network failure or
        // the 11s timeout rejects asynchronously — the surrounding try/catch
        // only catches synchronous throws. Swallow the rejection so logging
        // never surfaces its own error as an unhandled rejection (Bugsnag noise).
        axios({
          method: `post`,
          url: `${config.appDomain}/api/log`,
          timeout: 11000,
          data: { message, ...meta },
        }).catch(() => {});
      }
    } catch (err) {
      // do nothing
    }
  }

  audit(message, meta = {}) {
    this.log(message, meta, 'audit');
  }

  crit(message = '', meta = {}) {
    const logMessage = message.stack || message;
    this.log(logMessage, meta, 'crit');
  }

  error(message = '', meta = {}) {
    const logMessage = message.stack || message;
    this.log(logMessage, meta, 'error');
  }

  warn(message, meta = {}) {
    this.log(message, meta, 'warn');
  }

  info(message, meta = {}) {
    this.log(message, meta, 'info');
  }

  debug(message, meta = {}) {
    this.log(message, meta, 'debug');
  }

  startTime(label) {
    if (isClient()) return {};
    const hrstart = process.hrtime();
    return {
      hrstart,
      label,
    };
  }

  endTime({ hrstart, label } = {}, meta = {}) {
    if (isClient()) return;
    // process.hrtime(undefined) behaves like process.hrtime() and returns the
    // absolute monotonic clock, which would be logged as a ~21-day duration.
    if (!hrstart) return;
    const hrend = process.hrtime(hrstart);
    const time = Math.floor(hrend[0] * 1000 + hrend[1] / 1000000);
    if (time > 5000) {
      this.warn(`Execution time for ${label}: ${time} ms`, meta);
    } else {
      this.audit(`Execution time for ${label}: ${time} ms`, meta);
    }
  }

  startRequest(req) {
    if (!req) return {};
    if (req.path.startsWith('/_next/')) return {};
    if (req.path.startsWith('/static/')) return {};
    // Browser and password-manager discovery probes (passkey-endpoints,
    // change-password, the spec's deliberate 404 probe). They 404 through the
    // Next catch-all at ~4ms and were ~18% of all timing lines, diluting
    // UserWeb-ResponseTimeDistribution with non-renders. The handlers that do
    // serve /.well-known/ paths are registered ahead of the catch-all, so they
    // never reach this middleware either way.
    if (req.path.startsWith('/.well-known/')) return {};
    const startTime = this.startTime(req.path);
    this.audit(`Received request ${req.path}`, {
      headers: safeHeaders(req.headers),
    });
    return startTime;
  }

  endRequest(startTime, req) {
    if (!req) return;
    // Key off the start marker rather than re-reading req.path: Next's
    // handle() rewrites req.url for data requests
    // (/_next/data/<buildId>/tracks.json -> /tracks), so by the time we get
    // here the path no longer matches what startRequest() filtered on. Only
    // the marker tells us whether a timer was actually started.
    if (!startTime?.hrstart) return;
    this.audit(`Sent response ${startTime.label}`, {});
    this.endTime(startTime, {});
  }
}

module.exports = new Logger();
module.exports.redactFields = redactFields;
module.exports.safeHeaders = safeHeaders;
module.exports.withoutCredentialParams = withoutCredentialParams;
